import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import { format } from "date-fns";

const MARGIN = 10; // mm
const PT_TO_MM = 25.4 / 72;
const FONT = "times"; // jsPDF built-in Times – matches the Times New Roman templates
const BLOCK_DISPLAYS = ["block", "flex", "grid", "table", "list-item", "inline-block", "inline-flex"];

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

/** Wait for web fonts and every <img> inside the element to finish loading. */
export const waitForAssets = async (element) => {
  if (document.fonts?.ready) await document.fonts.ready;
  const images = Array.from(element.querySelectorAll("img"));
  await Promise.all(
    images.map((img) =>
      img.complete
        ? null
        : new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          }),
    ),
  );
};

/** "John Tan Payslip - Oct-2026.pdf" – safe even when toDate is missing/invalid. */
export const getPayslipFileName = (payslipDetails) => {
  const d = new Date(payslipDetails?.toDate);
  const period = Number.isNaN(d.getTime())
    ? `${payslipDetails?.procesMonth ?? ""}-${payslipDetails?.processYear ?? ""}`
    : format(d, "MMM-yyyy");
  const name = (payslipDetails?.fullName || "Employee").trim();
  return `${name} Payslip - ${period}.pdf`.replace(/[\\/:*?"<>|]/g, "");
};

const isHidden = (style) =>
  style.display === "none" || style.visibility === "hidden" || style.opacity === "0";

/**
 * Flatten the template into an ordered list of:
 *  - { type: "table", el }
 *  - { type: "img",   el }
 *  - { type: "text",  el }  (an element whose children are all inline)
 */
const collectBlocks = (root) => {
  const blocks = [];
  const visit = (el) => {
    const style = window.getComputedStyle(el);
    if (isHidden(style)) return;

    if (el.tagName === "TABLE") return blocks.push({ type: "table", el });
    if (el.tagName === "IMG") return blocks.push({ type: "img", el });

    const children = Array.from(el.children);
    const hasBlockChild = children.some((c) => {
      if (c.tagName === "IMG" || c.tagName === "TABLE") return true;
      return BLOCK_DISPLAYS.includes(window.getComputedStyle(c).display);
    });

    if (hasBlockChild) children.forEach(visit);
    else if (el.innerText.trim()) blocks.push({ type: "text", el });
  };
  Array.from(root.children).forEach(visit);
  return blocks;
};

/* ------------------------------------------------------------------ */
/* Mode 1: real PDF (selectable text, small file) using autoTable      */
/* ------------------------------------------------------------------ */

export const buildTablePdf = (element) => {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
  doc.setLineHeightFactor(1.15);

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const contentW = pageW - MARGIN * 2;
  const rootRect = element.getBoundingClientRect();
  const pxToMm = rootRect.width ? contentW / rootRect.width : 0.2646;
  const ctx = { y: MARGIN };

  const ensureSpace = (h) => {
    if (ctx.y + h > pageH - MARGIN && ctx.y > MARGIN) {
      doc.addPage();
      ctx.y = MARGIN;
    }
  };

  /* Text & images between tables are placed at their real on-screen
     position (scaled), so side-by-side layouts (address | logo) stay intact. */
  const drawSegment = (items) => {
    if (!items.length) return;
    const rects = items.map((b) => b.el.getBoundingClientRect());
    const segTop = Math.min(...rects.map((r) => r.top));
    const segBottom = Math.max(...rects.map((r) => r.bottom));
    const segH = (segBottom - segTop) * pxToMm;
    ensureSpace(segH);
    const baseY = ctx.y;

    items.forEach((block, i) => {
      const r = rects[i];
      const x = MARGIN + (r.left - rootRect.left) * pxToMm;
      const y = baseY + (r.top - segTop) * pxToMm;
      const w = r.width * pxToMm;

      if (block.type === "img") {
        try {
          doc.addImage(block.el, "PNG", x, y, w, r.height * pxToMm);
        } catch (err) {
          console.warn("Payslip PDF: skipped image (CORS?)", err);
        }
        return;
      }

      const style = window.getComputedStyle(block.el);
      const fontPx = parseFloat(style.fontSize) || 16;
      const lineHeightPx = parseFloat(style.lineHeight) || fontPx * 1.2;
      const weight = style.fontWeight;
      const bold = weight === "bold" || parseInt(weight, 10) >= 600;

      doc.setFont(FONT, bold ? "bold" : "normal");
      doc.setFontSize((fontPx * pxToMm) / PT_TO_MM); // keep font scaled with layout
      doc.setTextColor(0);

      const text = block.el.innerText.replace(/\n{2,}/g, "\n").trim();
      const lines = doc.splitTextToSize(text, w + 2); // +2mm tolerance for font metric differences

      const ta = style.textAlign;
      const align = ta === "center" ? "center" : ta === "right" || ta === "end" ? "right" : "left";
      const textX = align === "center" ? x + w / 2 : align === "right" ? x + w : x;
      const textY = y + (Math.max(0, lineHeightPx - fontPx) / 2) * pxToMm;

      doc.text(lines, textX, textY, { align, baseline: "top" });
    });

    ctx.y = baseY + segH + 2;
  };

  const drawTable = (table) => {
    autoTable(doc, {
      html: table,
      startY: ctx.y,
      useCss: true, // keeps bold, alignment, background colours and borders from the template
      theme: "plain",
      margin: { left: MARGIN, right: MARGIN, top: MARGIN, bottom: MARGIN },
      styles: {
        font: FONT,
        fontSize: 10,
        textColor: 0,
        lineColor: 0,
        lineWidth: 0.2,
        cellPadding: 1.5,
        valign: "middle",
        overflow: "linebreak",
      },
      headStyles: { fontStyle: "bold" },
      rowPageBreak: "avoid",
      didParseCell: (data) => {
        data.cell.styles.font = FONT; // CSS font-family isn't usable by jsPDF
      },
    });
    ctx.y = doc.lastAutoTable.finalY + 3;
  };

  let segment = [];
  collectBlocks(element).forEach((block) => {
    if (block.type === "table") {
      drawSegment(segment);
      segment = [];
      drawTable(block.el);
    } else {
      segment.push(block);
    }
  });
  drawSegment(segment);

  return doc;
};

/* ------------------------------------------------------------------ */
/* Mode 2: pixel-perfect image PDF (html2canvas)                        */
/* ------------------------------------------------------------------ */

export const buildImagePdf = async (element) => {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false,
  });

  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const usableW = pageW - MARGIN * 2;
  const usableH = pageH - MARGIN * 2;

  let imgW = usableW;
  let imgH = (canvas.height * imgW) / canvas.width;

  // Slightly too tall? shrink to keep it on one page instead of splitting
  if (imgH > usableH && imgH <= usableH * 1.25) {
    imgW = (usableH / imgH) * imgW;
    imgH = usableH;
  }
  const x = (pageW - imgW) / 2;
  const imgData = canvas.toDataURL("image/jpeg", 0.95);

  // Multi-page: draw the same image shifted up on each page, then mask the margins
  for (let offset = 0; offset < imgH - 0.5; offset += usableH) {
    if (offset > 0) doc.addPage();
    doc.addImage(imgData, "JPEG", x, MARGIN - offset, imgW, imgH, "payslip", "FAST");
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageW, MARGIN, "F");
    doc.rect(0, pageH - MARGIN, pageW, MARGIN, "F");
  }

  return doc;
};

/* ------------------------------------------------------------------ */
/* Public entry point                                                   */
/* ------------------------------------------------------------------ */

/**
 * @param {HTMLElement} element  rendered payslip template
 * @param {"table"|"image"} mode "table" = autoTable (default), "image" = html2canvas
 * @returns {Promise<jsPDF>}
 */
export const buildPayslipPdf = async (element, mode = "table") => {
  if (!element) throw new Error("Payslip element not found");
  await waitForAssets(element);

  if (mode === "table" && element.querySelector("table")) {
    return buildTablePdf(element);
  }
  return buildImagePdf(element);
};